import { last, trim } from 'lodash';
import { compact, flatten, sortBy } from 'lodash/fp';

import { getJsonSchemaType } from 'dashboard/lib/workflows';
import {
  appendedMethodChain,
  Chain,
  EMPTY_METHOD_CHAIN,
  methodChainToString,
  parseWithPartialOption,
} from 'dashboard/lib/workflows/method-chain';
import { getSchemaId, resolveJssRef } from 'dashboard/lib/workflows/platform-object';
import { WorkflowFieldObjectKey } from 'dashboard/models/workflow';
import { FunctionJsonSchema, ObjectSchemaMap } from 'dashboard/redux/slices/object-schemas';
import {
  JsonSchemaAllTypes,
  JsonSchemaCustomTypes,
  JsonSchemaStringifiableTypes,
} from 'lib/fields';
import {
  EMPTY_JSON_SCHEMA_OBJECT,
  isFunctionSchema,
  isGenericArrayItemSchema,
  isJsonSchemaObject,
  isJsonSchemaRef,
  JsonSchema,
  JsonSchemaArray,
  JsonSchemaBasicTypes,
  JsonSchemaObject,
  JsonSchemaTypes,
} from 'lib/json-schema';
import { assertNever } from 'lib/utils/types';

export type Option = {
  label: string;
  value: string;
  property: string;
  schema: JsonSchema;
  methodChain: Chain | null;
  utility?: boolean;
};

type PossibleType = JsonSchemaTypes | JsonSchemaCustomTypes | WorkflowFieldObjectKey;
type PossibleArrayType = JsonSchemaBasicTypes | JsonSchemaTypes.Object | WorkflowFieldObjectKey;

const isMatchingType = (
  schema: JsonSchema,
  allowedTypes: PossibleType[],
  allowedArrayTypes: PossibleArrayType[],
) => {
  if (allowedTypes.length === 0) {
    return true;
  }
  const jsonSchemaType = getJsonSchemaType(schema);
  // If strings are allowed and the field is string-able, let it through.
  if (
    allowedTypes.some((schemaType) => schemaType === JsonSchemaTypes.String) &&
    JsonSchemaStringifiableTypes.some((schemaType) => schemaType === jsonSchemaType)
  ) {
    return true;
  } else if (jsonSchemaType === JsonSchemaAllTypes.Array) {
    if (
      !allowedTypes.includes(JsonSchemaAllTypes.Array) ||
      // Don't recurse on array, otherwise we risk matching on objects within the array
      (allowedArrayTypes?.length &&
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        !allowedArrayTypes.some((schemaType) => schemaType === getJsonSchemaType(schema.items)))
    ) {
      return false;
    }
  } else if (
    jsonSchemaType !== JsonSchemaAllTypes.Object &&
    !allowedTypes.some((schemaType) => schemaType === jsonSchemaType)
  ) {
    return false;
  }
  return true;
};

// isValidOptionHelper returns true if the schema provided is (in order of precedence)
// 1) not a disallowed by name
// AND 2) not a disallowed type
// AND 3) allowed field by name (either explicitly or implicitly)
// at each level (we recurse down for object types), we check that the fields at the level fit the above 1,2 and 3
//
// 1) is straight forward. if a field is named as disallowed it has the most precedence
// 2) the field is disallowed by type if the allowedTypes are specified AND
//    - if the field is an array, then
//       - arrays are in allowedTypes
//       - AND (its `items` type is in `allowedArrayTypes` OR `allowedArrayTypes` is empty)
//    - if the field is an object, we do nothing since the recursion is handled at the `isValidOptionHelper` level
//    - else, the field type appears in allowedTypes
// 3) we can be allowed by name
//    - explicitly, which means the field is named explicitly as an allowedFieldPath (this has precedence)
//    - implicitly, which means that there are no fields explicitly named in allowedFieldPath
//      - an object field can be allowed implicitly if it has at least one valid children
const isValidOptionHelper = (
  schema: JsonSchema,
  allowedFieldPaths: Set<string>,
  disallowedFieldPaths: Set<string>,
  allowedTypes: PossibleType[],
  allowedArrayTypes: PossibleArrayType[],
  path: string[],
): boolean => {
  const dotPath = path.join('.');
  if (disallowedFieldPaths.has(dotPath)) {
    return false;
  }
  if (!isMatchingType(schema, allowedTypes, allowedArrayTypes)) {
    return false;
  }
  if (allowedFieldPaths.has(dotPath)) {
    return true;
  }
  // if we are here it means
  // - our type is valid
  // - we are not disallowed
  // - we are not explicitly allowed
  // if we are here, we are only valid if
  // - we are an object and at least one of our properties are valid
  // OR, we are not an object and allowedFieldPaths is empty
  const jsonSchemaType = getJsonSchemaType(schema);
  if (jsonSchemaType === JsonSchemaTypes.Object) {
    return (
      'properties' in schema &&
      schema.properties != null &&
      Object.entries(schema.properties).some(([prop, propSchema]) =>
        isValidOptionHelper(
          propSchema,
          allowedFieldPaths,
          disallowedFieldPaths,
          allowedTypes,
          allowedArrayTypes,
          [...path, prop],
        ),
      )
    );
  }
  return allowedFieldPaths.size === 0;
};

const isValidOption = (
  schema: JsonSchema,
  allowedFieldPaths: string[],
  disallowedFieldPaths: string[],
  allowedTypes: PossibleType[],
  allowedArrayTypes: PossibleArrayType[],
  path: string[] = [],
): boolean => {
  const allowedFieldPathsSet =
    allowedFieldPaths == null ? new Set<string>() : new Set<string>([...allowedFieldPaths]);
  const disallowedFieldPathsSet =
    disallowedFieldPaths == null ? new Set<string>() : new Set<string>([...disallowedFieldPaths]);
  if (
    allowedFieldPathsSet.size === 0 &&
    disallowedFieldPathsSet.size === 0 &&
    allowedTypes.length === 0 &&
    allowedArrayTypes.length === 0
  ) {
    return true;
  }
  return isValidOptionHelper(
    schema,
    allowedFieldPathsSet,
    disallowedFieldPathsSet,
    allowedTypes ?? [],
    allowedArrayTypes ?? [],
    path,
  );
};

export const getSchemaOptions = ({
  option,
  optionFilters,
  platformObjectJsonSchema = {},
}: {
  option: {
    value?: Option['value'];
    schema: Option['schema'];
    methodChain: Option['methodChain'];
  };
  optionFilters?: OptionFilters;
  platformObjectJsonSchema: ObjectSchemaMap | undefined;
  primitiveUtilities: ObjectSchemaMap;
}): Option[] => {
  const { schema } = option;
  const resolvedSchema = isJsonSchemaRef(schema)
    ? resolveJssRef(schema, platformObjectJsonSchema)
    : schema;
  const schemaType = getJsonSchemaType(resolvedSchema);

  const schemaOptions = (() => {
    switch (schemaType) {
      case JsonSchemaAllTypes.Object: {
        return Object.entries((resolvedSchema as JsonSchemaObject).properties || {}).map(
          ([prop, propSchema]) => {
            const methodChain = appendedMethodChain(option.methodChain || EMPTY_METHOD_CHAIN, prop);
            return {
              label: methodChainToString(methodChain),
              value: methodChainToString(methodChain),
              property: prop,
              schema: propSchema as JsonSchema,
              methodChain,
            };
          },
        );
      }
      case JsonSchemaAllTypes.Array:
      case JsonSchemaAllTypes.Boolean:
      case JsonSchemaAllTypes.Date:
      case JsonSchemaAllTypes.Enum:
      case JsonSchemaAllTypes.File:
      case JsonSchemaAllTypes.GroupedEnum:
      case JsonSchemaAllTypes.Integer:
      case JsonSchemaAllTypes.MultiEnum:
      case JsonSchemaAllTypes.Relation:
      case JsonSchemaAllTypes.Number:
      case JsonSchemaAllTypes.String:
      case JsonSchemaAllTypes.Unknown:
        return [];
      default:
        assertNever(schemaType);
    }
  })();

  const containsFilters = optionFilters && !!flatten(compact(Object.values(optionFilters))).length;
  const finalOptions = containsFilters
    ? filterForAllowedSchemaOptions(schemaOptions, optionFilters, platformObjectJsonSchema)
    : schemaOptions;
  return sortBy('property', finalOptions) as Option[];
};

export const formatDisplayValue = (value: any) => {
  return typeof value === 'string' ? value : trim(JSON.stringify(value, null, 1), '"');
};

export const parseMethodChain = (path: string): Chain | null => {
  const { methodChain, partial, valid } = parseWithPartialOption(path);
  if (!valid) {
    return null;
  }

  if (partial.length > 0) {
    return appendedMethodChain(methodChain, partial);
  }

  return methodChain;
};

export const getSchemaForFieldPath = ({
  path,
  initialSchema,
  platformObjectJsonSchema = {},
  primitiveUtilities = {},
}: {
  path: string;
  initialSchema: JsonSchema;
  platformObjectJsonSchema?: ObjectSchemaMap;
  primitiveUtilities?: ObjectSchemaMap;
}): {
  schema: JsonSchema | null;
  propertyName: string | null;
} => {
  if (!initialSchema || (!('type' in initialSchema) && !('$ref' in initialSchema))) {
    return { schema: null, propertyName: null };
  }

  const schema: JsonSchema = isJsonSchemaRef(initialSchema)
    ? resolveJssRef(initialSchema, platformObjectJsonSchema)
    : initialSchema;

  if (path === '') {
    return { schema: null, propertyName: null };
  }
  const methodChain = parseMethodChain(path);
  if (!methodChain) {
    return { schema: null, propertyName: null };
  }
  const result = methodChain.links.reduce<JsonSchema | null>((currentSchema, link) => {
    return nextLinkSchema({
      currentSchema,
      nextLink: link.name,
      platformObjectJsonSchema,
      primitiveUtilities,
    });
  }, schema);

  const propertyName = result ? last(methodChain.links)?.name || null : null;
  return { schema: result, propertyName };
};

export const nextLinkSchema = ({
  currentSchema,
  nextLink,
  platformObjectJsonSchema,
  primitiveUtilities,
}: {
  currentSchema: JsonSchema | null;
  nextLink: string;
  platformObjectJsonSchema: ObjectSchemaMap | undefined;
  primitiveUtilities: ObjectSchemaMap;
}): JsonSchema | null => {
  if (!currentSchema) {
    return null;
  }
  if (isJsonSchemaRef(currentSchema)) {
    currentSchema = resolveJssRef(currentSchema, platformObjectJsonSchema);
  }
  if (!('type' in currentSchema)) {
    return null;
  }

  const schemaId = getSchemaId(currentSchema.type);
  if (schemaId in primitiveUtilities) {
    const utilityReturnType = getPrimitiveUtilitiesReturnSchema(
      currentSchema,
      nextLink,
      primitiveUtilities[schemaId],
    );
    if (utilityReturnType) {
      return utilityReturnType;
    }
  }
  if (currentSchema.type === JsonSchemaTypes.Array) {
    return currentSchema.items;
  }
  if ('properties' in currentSchema) {
    return currentSchema.properties[nextLink];
  }
  return null;
};

const getPrimitiveUtilitiesReturnSchema = (
  object: JsonSchema,
  message: string,
  schema: JsonSchemaObject<JsonSchema | FunctionJsonSchema>,
): JsonSchema | null => {
  if (!(message in schema.properties)) {
    return null;
  }

  const utilitySchema = schema.properties[message];

  const returnSchema = isFunctionSchema(utilitySchema)
    ? utilitySchema.properties.returnType
    : utilitySchema;

  if (isGenericArrayItemSchema(object, returnSchema)) {
    return object.items;
  }

  return 'genericType' in returnSchema ? EMPTY_JSON_SCHEMA_OBJECT : returnSchema;
};

/**
 * @param path
 * @param allowedFieldPaths - can be a complete path to a base type field
 * (e.g. 'data.some.string_field'), or a partial path to an object ('data')
 * @param fieldPathsAreStrictMatch - true if we only want to match exact paths in allowedFieldPaths and disallowedFieldPaths
 */
const isAllowedPath = (path: string, allowedFieldPaths: string[]) => {
  return allowedFieldPaths.some((fieldPath) => {
    const valueIsPartialMatch = fieldPath.startsWith(path);
    const valueIsNestedProp = path.startsWith(`${fieldPath}.`);
    return valueIsPartialMatch || valueIsNestedProp;
  });
};

/**
 * @param path
 * @param disallowedFieldPaths - can be a complete path to a base type field
 * (e.g. 'data.some.string_field'), or a partial path to an object ('data')
 */
const isDisallowedPath = (path: string, disallowedFieldPaths: string[]) => {
  return disallowedFieldPaths.some((fieldPath) => {
    const valueIsMatch = fieldPath === path;
    const valueIsNestedProp = path.startsWith(`${fieldPath}.`);
    return valueIsMatch || valueIsNestedProp;
  });
};

export const isTypeMatch = ({
  fieldJsonSchema,
  types,
  platformObjectJsonSchema = {},
  arrayTypes,
}: {
  fieldJsonSchema: JsonSchema;
  types: PossibleType[];
  platformObjectJsonSchema: ObjectSchemaMap;
  arrayTypes?: PossibleArrayType[];
}): boolean => {
  const resolvedSchema = isJsonSchemaRef(fieldJsonSchema)
    ? resolveJssRef(fieldJsonSchema, platformObjectJsonSchema)
    : fieldJsonSchema;
  const fieldJsonSchemaType = getJsonSchemaType(resolvedSchema);
  switch (fieldJsonSchemaType) {
    case JsonSchemaAllTypes.Object: {
      const objectSchema = resolvedSchema as JsonSchemaObject;
      if (
        types.includes(JsonSchemaTypes.Object) ||
        (objectSchema.objectKey && types.includes(objectSchema.objectKey))
      ) {
        return true;
      }
      return Object.values(objectSchema.properties || {}).some((propJsonSchema) =>
        isTypeMatch({
          fieldJsonSchema: propJsonSchema,
          types,
          platformObjectJsonSchema,
          arrayTypes,
        }),
      );
    }
    case JsonSchemaAllTypes.Array: {
      const arraySchema = resolvedSchema as JsonSchemaArray;
      const itemSchema = isJsonSchemaRef(arraySchema.items)
        ? resolveJssRef(arraySchema.items, platformObjectJsonSchema)
        : arraySchema.items;
      return (
        types.includes(JsonSchemaAllTypes.Array) &&
        // Don't recurse on array, otherwise we risk matching on objects within the array
        !!arrayTypes &&
        arrayTypes.some(
          (schemaType) =>
            schemaType === getJsonSchemaType(itemSchema) ||
            (isJsonSchemaObject(itemSchema) && schemaType === itemSchema.objectKey),
        )
      );
    }
    case JsonSchemaAllTypes.String:
    case JsonSchemaAllTypes.Number:
    case JsonSchemaAllTypes.Boolean:
    case JsonSchemaAllTypes.Integer:
    case JsonSchemaAllTypes.Date:
    case JsonSchemaAllTypes.Enum:
    case JsonSchemaAllTypes.MultiEnum:
    case JsonSchemaAllTypes.GroupedEnum:
    case JsonSchemaAllTypes.Unknown: {
      return types.some((schemaType) => schemaType === fieldJsonSchemaType);
    }
    default:
      return false;
  }
};

export interface OptionFilters {
  allowedArrayTypes?: PossibleArrayType[];
  allowedFieldPaths?: string[];
  allowedTypes?: PossibleType[];
  disallowedFieldPaths?: string[];
  fieldPathsAreStrictMatch?: boolean;
}

const filterForAllowedSchemaOptions = (
  options: Option[] = [],
  {
    allowedArrayTypes,
    allowedFieldPaths,
    allowedTypes,
    disallowedFieldPaths,
    fieldPathsAreStrictMatch,
  }: OptionFilters,
  platformObjectJsonSchema: ObjectSchemaMap = {},
) => {
  let filteredOptions = options;

  if (fieldPathsAreStrictMatch) {
    if (allowedFieldPaths?.length || disallowedFieldPaths?.length || allowedTypes?.length) {
      filteredOptions = filteredOptions.filter((option) =>
        isValidOption(
          option.schema,
          allowedFieldPaths ?? [],
          disallowedFieldPaths ?? [],
          allowedTypes ?? [],
          allowedArrayTypes ?? [],
          option.value.split('.'),
        ),
      );
    }
  } else {
    if (allowedFieldPaths?.length) {
      filteredOptions = filteredOptions.filter((option) =>
        isAllowedPath(option.value, allowedFieldPaths),
      );
    }
    if (disallowedFieldPaths?.length) {
      filteredOptions = filteredOptions.filter(
        (option) => !isDisallowedPath(option.value, disallowedFieldPaths),
      );
    }
    if (allowedTypes?.length) {
      filteredOptions = filteredOptions.filter((option) =>
        isTypeMatch({
          fieldJsonSchema: option.schema,
          types: allowedTypes,
          platformObjectJsonSchema,
          arrayTypes: allowedArrayTypes,
        }),
      );
    }
  }
  return filteredOptions;
};

export const getOptionForValue = ({
  value,
  jsonSchema,
  schemaForFieldPath,
}: {
  value: string;
  jsonSchema: JsonSchema;
  schemaForFieldPath: (
    path: string,
    initialSchema: JsonSchema,
  ) => { schema: JsonSchema | null; propertyName: string | null };
}): Option | null => {
  const { schema, propertyName } = schemaForFieldPath(value, jsonSchema);
  if (!schema || !propertyName) {
    return null;
  }

  const methodChain = parseMethodChain(value);
  return {
    label: methodChain ? methodChainToString(methodChain) : value,
    value: methodChain ? methodChainToString(methodChain) : value,
    property: propertyName,
    schema,
    methodChain,
  };
};

export const validatePath = ({
  path,
  jsonSchema,
  platformObjectJsonSchema = {},
  primitiveUtilities = {},
  optionFilters,
}: {
  path: string;
  jsonSchema: JsonSchema;
  platformObjectJsonSchema: ObjectSchemaMap | undefined;
  primitiveUtilities: ObjectSchemaMap;
  optionFilters?: OptionFilters;
}) => {
  const schemaOptions = getSchemaOptions({
    option: { schema: jsonSchema, value: '', methodChain: parseMethodChain('') },
    optionFilters,
    platformObjectJsonSchema,
    primitiveUtilities,
  });
  const { methodChain, partial } = parseWithPartialOption(path);

  const matchedSchemaOptions = methodChain.links.reduce<Option[]>((currentSchema, link) => {
    if (currentSchema.length === 0) {
      return [];
    }
    const optionMatch = currentSchema.find((option) => option.property === link.name);
    if (optionMatch) {
      return getSchemaOptions({
        option: optionMatch,
        optionFilters,
        platformObjectJsonSchema,
        primitiveUtilities,
      });
    }
    return [];
  }, schemaOptions);

  const optionsWithPartiallyMatchedFinalLink = matchedSchemaOptions.filter((option) =>
    option.property.includes(partial),
  );
  return {
    valid: optionsWithPartiallyMatchedFinalLink.length > 0,
    schemaOptions: optionsWithPartiallyMatchedFinalLink,
    partial,
  };
};
